import axios from 'axios';
import qs from 'qs';

import { filterEmptyParams, relativeUrlResolver } from '@/services/utils';

import type { AssetApiRecord } from './assets.types';
import {
    type LabelChange,
    type LabelledAsset,
    planLabelChanges,
    toLabelledAssets,
} from './bulkLabels';
import { type DamBulkFailure, failureReasonOf } from './damBulk';
import { type AssetLabel, assignLabel, removeLabel } from './labels.queries';

const LABELS_URL = '/api/v2/labels/';
const ASSETS_URL = '/api/v2/assets/';
/** `POST /labels` requires a colour the UI does not show; a neutral default until LAX-10489 (D-038). */
const DAM_DEFAULT_LABEL_COLOR = '#6B7280';

const createLabel = async (name: string): Promise<AssetLabel> =>
    (await axios.post(relativeUrlResolver(LABELS_URL), { name, color: DAM_DEFAULT_LABEL_COLOR })).data;

const renameLabel = async (id: string, name: string): Promise<AssetLabel> =>
    (await axios.put(relativeUrlResolver(`${LABELS_URL}${encodeURIComponent(id)}`), { name })).data;

/** Deletes the label only; its `assetLabel:<id>` tags stay on the assets until LAX-10487 (D-038). */
const deleteLabel = async (id: string) => {
    await axios.delete(relativeUrlResolver(`${LABELS_URL}${encodeURIComponent(id)}`));
};

/** Labels the given assets carry; `items` restricts the search to their primary keys. */
const fetchLabelledAssets = async (keys: string[]): Promise<LabelledAsset[]> => {
    if (keys.length === 0) {
        return [];
    }
    const response = await axios.get<AssetApiRecord[]>(relativeUrlResolver(ASSETS_URL), {
        params: { items: keys, fields: ['primaryKey__', 'tags__'], count: keys.length },
        paramsSerializer: (params) => qs.stringify(filterEmptyParams(params), { arrayFormat: 'repeat' }),
    });
    return toLabelledAssets(response.data ?? []);
};

interface DamLabelChangesResult {
    /** Assets with at least one change that failed, with the reason of the first failure. */
    failed: DamBulkFailure[];
}

/**
 * Runs the label changes one after the other (no bulk endpoint until LAX-10482); failures do not stop
 * the run. Parallel label calls on several assets run into datastore contention on the server.
 */
const runLabelChanges = async (changes: LabelChange[], onProgress?: (done: number) => void): Promise<DamLabelChangesResult> => {
    const failed = new Map<string, DamBulkFailure>();
    let done = 0;
    for (const change of changes) {
        try {
            const apply = change.action === 'assign' ? assignLabel : removeLabel;
            await apply({ primaryKey: change.primaryKey, labelId: change.labelId });
        } catch (error) {
            if (!failed.has(change.primaryKey)) {
                failed.set(change.primaryKey, { primaryKey: change.primaryKey, reason: failureReasonOf(error) });
            }
        }
        done += 1;
        onProgress?.(done);
    }
    return { failed: [...failed.values()] };
};

export { createLabel, deleteLabel, fetchLabelledAssets, planLabelChanges, renameLabel, runLabelChanges };
export type { DamLabelChangesResult, LabelChange, LabelledAsset };
